import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { createFormOrder } from '@app/api/action/Order';
import { RootReducer, Store } from '@app/app.reducers';
import {
	getOrderFormSubmitError,
	getOrderFormSubmitProcessing,
	getOrderFormSubmitSuccess,
	resetOrderFormStatus,
} from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { getId as getStorefrontId } from '@app/shared/reducers/storefront.reducer';
import { popErrorToast } from '@app/shared/reducers/toast.reducer';
import { FormErrors, formValidationErrors$, ValidationMessages } from '@app/shared/utils/form-helper';
import { I18nService } from '@app/shared/utils/i18n.service';
import { filterTruthy } from '@app/shared/utils/util';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner';
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { first } from 'rxjs/operators';

@UntilDestroy()
@Component({
	selector: 'g-product-form-order-dialog',
	templateUrl: './product-form-order-dialog.component.html',
	styleUrls: ['./product-form-order-dialog.component.scss'],
	standalone: false,
})
export class ProductFormOrderDialogComponent implements OnInit {
	protected store = inject<Store<RootReducer.State>>(Store);
	private readonly fb = inject(UntypedFormBuilder);
	private readonly sanitizer = inject(DomSanitizer);
	private readonly modal = inject(NgbActiveModal);
	private readonly i18n = inject(I18nService);

	static readonly MAX_WIDTH = '880px';
	readonly icons = { faXmark, faSpinner };
	readonly dialogData?: { product: api.ProductDto };
	productFormOrderForm: UntypedFormGroup;
	formErrors$: FormErrors;
	validationMessages: ValidationMessages;
	product: api.ProductDto;
	isProcessing: boolean;
	isSuccess$: Observable<boolean>;
	formTitle: string;
	formDescription: SafeHtml;
	isCompanyVisible: boolean;
	private storeId: number;

	constructor() {
		this.validationMessages = {
			firstName: {
				required: $localize`:@@ContactInfoFirstNameRequired:First name is required`,
				maxlength: $localize`:@@ContactInfoFirstNameMaxLength:First name cannot be over 255 characters long`,
			},
			lastName: {
				required: $localize`:@@ContactInfoLastNameRequired:Last name is required`,
				maxlength: $localize`:@@ContactInfoLastNameMaxLength:Last name cannot be over 255 characters long`,
			},
			email: {
				required: $localize`:@@ContactInfoEmailRequired:Email is required`,
				maxlength: $localize`:@@ContactInfoEmailMaxLength:Email cannot be over 255 characters long`,
				email: $localize`:@@ContactInfoEmailFormat:Email has to be in right format`,
				oneValueRequired: $localize`:@@ContactRequestOneValueRequired:Either email or phone number is required`,
			},
			phone: {
				required: $localize`:@@ContactInfoPhoneRequired:Phone number is required`,
				pattern: $localize`:@@ContactInfoPhoneFormat:Phone number has to be in valid format`,
				oneValueRequired: $localize`:@@ContactRequestOneValueRequired:Either email or phone number is required`,
			},
			streetName: {
				required: $localize`:@@ContactInfoStreetNameRequired:Street name is required`,
				maxlength: $localize`:@@ContactInfoStreetNameMaxLength:Street name cannot be over 50 characters long`,
			},
			zipCode: {
				required: $localize`:@@ContactInfoZipCodeRequired:Zip code is required`,
				minlength: $localize`:@@ContactInfoZipCodeMinLength:Zip code has to be 5 characters`,
				maxlength: $localize`:@@ContactInfoZipCodeMaxLength:Zip code has to be 5 characters`,
			},
			city: {
				required: $localize`:@@ContactInfoCityRequired:City is required`,
				maxlength: $localize`:@@ContactInfoCityMaxLength:City cannot be over 255 characters long`,
			},
			company: {
				maxlength: $localize`:@@ContactInfoCompanyMaxLength:Company name cannot be over 255 characters long`,
			},
		};
	}

	ngOnInit(): void {
		this.buildForm();
		this.store
			.select(getStorefrontId)
			.pipe(first())
			.subscribe((storefrontId) => (this.storeId = storefrontId));
		this.product = this.dialogData?.product;
		this.formTitle = this.i18n.getText(this.product?.metadata?.form_title);
		this.formDescription = this.sanitizer.bypassSecurityTrustHtml(
			this.i18n.getText(this.product?.metadata?.form_description),
		);
		this.isCompanyVisible = this.product?.metadata?.ask_company_name === true;
		this.store
			.select(getOrderFormSubmitProcessing)
			.pipe(untilDestroyed(this))
			.subscribe((isProcessing) => (this.isProcessing = isProcessing === true));
		this.store
			.select(getOrderFormSubmitError)
			.pipe(filterTruthy, untilDestroyed(this))
			.subscribe(() =>
				this.store.dispatch(
					popErrorToast({
						title: $localize`:@@ToastProductFormError:Failed to submit form. Try again later.`,
					}),
				),
			);
		this.isSuccess$ = this.store.select(getOrderFormSubmitSuccess);
	}

	onSubmit(): void {
		this.store.dispatch(
			createFormOrder({
				body: {
					first_name: this.productFormOrderForm.value.firstName,
					last_name: this.productFormOrderForm.value.lastName,
					email: this.productFormOrderForm.value.email || null,
					phone:
						typeof this.productFormOrderForm.value.phone === 'string'
							? this.productFormOrderForm.value.phone.replaceAll(' ', '')
							: null,
					message: this.productFormOrderForm.value.message,
					street_name: this.productFormOrderForm.value.streetName,
					zip_code: this.productFormOrderForm.value.zipCode,
					city: this.productFormOrderForm.value.city,
					company: this.productFormOrderForm.value.company || null,
					product_id: this.product.id!,
					store_id: this.storeId, // TODO: Remove.
				},
			}),
		);
	}

	closeDialog(): void {
		// Reset form status when dialog is closed.
		this.store.dispatch(resetOrderFormStatus());
		this.modal.close();
	}

	private buildForm() {
		this.productFormOrderForm = this.fb.group({
			firstName: ['', [Validators.required, Validators.maxLength(255)]],
			lastName: ['', [Validators.required, Validators.maxLength(255)]],
			email: ['', [Validators.maxLength(255), Validators.email, Validators.required]],
			phone: ['', [Validators.pattern(/^(\+?)([0-9 ]+)$/), Validators.required]],
			streetName: ['', [Validators.required, Validators.maxLength(255)]],
			zipCode: [
				'',
				[
					Validators.required,
					Validators.minLength(5),
					Validators.maxLength(5),
					Validators.pattern(/^[0-9 -]*$/),
				],
			],
			city: ['', [Validators.required, Validators.maxLength(255)]],
			company: ['', [Validators.maxLength(255)]],
		});

		this.formErrors$ = formValidationErrors$(this.productFormOrderForm, this.validationMessages);
	}
}
